import { useState, useRef, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Treemap, ResponsiveContainer } from "recharts";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Zap, Target } from "lucide-react";

interface MarketStats {
  total3mo: number;
  weeklyAvg: number;
  categories: { slug: string; count: number }[];
  districts:  { district: string; count: number; schoolCount: number }[];
  schoolTypes: { type: string; count: number }[];
  categorySchoolTypes: Record<string, { type: string; count: number }[]>;
  categoryDistricts: Record<string, { district: string; count: number }[]>;
}

// ── Category metadata ─────────────────────────────────────────────────────

const CAT_INFO: Record<string, { name: string; color: string; emoji: string }> = {
  "professional-services":      { name: "專業服務",       color: "#6366f1", emoji: "💼" },
  "it":                         { name: "資訊科技",       color: "#06b6d4", emoji: "💻" },
  "engineering":                { name: "工程",           color: "#3b82f6", emoji: "🔧" },
  "study-tour":                 { name: "遊學活動",       color: "#8b5cf6", emoji: "✈️" },
  "catering":                   { name: "午膳服務",       color: "#f59e0b", emoji: "🍱" },
  "printing":                   { name: "印刷",           color: "#64748b", emoji: "🖨️" },
  "school-bus":                 { name: "校巴服務",       color: "#f97316", emoji: "🚌" },
  "uniform":                    { name: "校服",           color: "#ec4899", emoji: "👕" },
  "cleaning":                   { name: "清潔",           color: "#10b981", emoji: "🧹" },
  "security":                   { name: "保安",           color: "#1e40af", emoji: "🛡️" },
  "elevator":                   { name: "電梯",           color: "#ef4444", emoji: "🛗" },
  "gardening":                  { name: "園藝",           color: "#22c55e", emoji: "🌱" },
  "air-conditioning":           { name: "冷氣",           color: "#0ea5e9", emoji: "❄️" },
  "fire-safety":                { name: "消防",           color: "#dc2626", emoji: "🔥" },
  "growth-sky":                 { name: "成長的天空",     color: "#14b8a6", emoji: "🌤️" },
  "sports-music":               { name: "運動音樂",       color: "#f43f5e", emoji: "🎵" },
  "social-work-counseling":     { name: "社工心理輔導",   color: "#a855f7", emoji: "🤝" },
  "ai-robotics":                { name: "AI機械人",       color: "#7c3aed", emoji: "🤖" },
  "speech-therapy-special-ed":  { name: "言語治療特殊教育", color: "#0891b2", emoji: "💬" },
  "others":                     { name: "其他服務",       color: "#9ca3af", emoji: "📦" },
};

// ── District map positions (in 560×430 SVG viewBox) ──────────────────────

// Positions derived from land-pixel centroids in the 1200×896 map image
// (xMidYMid slice → scale=0.4799, offset_x=7.9)
const DISTRICT_POS: Record<string, { x: number; y: number; region: "NT" | "KLN" | "HKI" | "Islands" }> = {
  "元朗區":   { x: 189, y: 108, region: "NT" },
  "屯門區":   { x: 115, y: 171, region: "NT" },
  "荃灣區":   { x: 262, y: 189, region: "NT" },
  "葵青區":   { x: 288, y: 209, region: "NT" },
  "北區":     { x: 293, y:  67, region: "NT" },
  "大埔區":   { x: 347, y: 137, region: "NT" },
  "沙田區":   { x: 332, y: 187, region: "NT" },
  "西貢區":   { x: 475, y: 142, region: "NT" },
  "深水埗區": { x: 312, y: 225, region: "KLN" },
  "油尖旺區": { x: 326, y: 260, region: "KLN" },
  "九龍城區": { x: 362, y: 235, region: "KLN" },
  "黃大仙區": { x: 385, y: 221, region: "KLN" },
  "觀塘區":   { x: 427, y: 236, region: "KLN" },
  "中西區":   { x: 313, y: 295, region: "HKI" },
  "灣仔區":   { x: 345, y: 290, region: "HKI" },
  "東區":     { x: 400, y: 300, region: "HKI" },
  "南區":     { x: 335, y: 328, region: "HKI" },
  "離島區":   { x: 100, y: 304, region: "Islands" },
};

const REGION_COLOR: Record<string, string> = {
  NT: "#16a34a", KLN: "#d97706", HKI: "#0284c7", Islands: "#7c3aed",
};
const REGION_LABEL: Record<string, string> = {
  NT: "新界", KLN: "九龍", HKI: "香港島", Islands: "離島",
};

// ── Value examples ────────────────────────────────────────────────────────

const VALUE_EX = [
  {
    id: "catering", name: "🍱 午膳供應商", color: "#f59e0b",
    steps: [
      "每日400份午膳",
      "每份 $30",
      "全年供應200日",
      "合約年期 3年",
    ],
    formula: "400 × $30 × 200日 × 3年",
    total: 8_000_000,
    note: "學校為半官方機構，付款穩定，合約期通常為3年，收入有保障。",
    reference: { label: "例子參考：2025年4月田家炳中學 — 午膳供應服務（三年合約）", url: "https://web.tkpss.edu.hk/v3/00_about/tender/lunch_2025-2028_1.pdf" },
  },
  {
    id: "school-bus", name: "🚌 校車服務商", color: "#3b82f6",
    steps: [
      "學生總數 300位",
      "預計乘搭學生 50%（150位）",
      "平均雙程月費 $1,000/月",
      "全年服務 10個月",
      "合約年期 3年",
    ],
    formula: "300 × 50% × $1,000 × 10月 × 3年",
    total: 4_500_000,
    note: "校巴服務為長期穩定合約，學生數目固定，每月收入有保障，一旦得標即可持續三年穩定經營。",
    reference: { label: "例子參考：2026年4月 荃灣商會學校 — 2026-2027、2027-2028及2028-2029學年校巴服務", url: "https://www.twtaps.edu.hk/pdf/tender_documents/QT009-2025_承投提供「2026-2027、2027-2028及2028-2029學年校巴」服務.pdf" },
  },
  {
    id: "cleaning", name: "🧹 清潔服務商", color: "#6366f1",
    steps: [
      "清潔員工 4位",
      "員工月薪 $6,000/月（每日3小時）",
      "清潔劑消耗品 $500/月",
      "全年服務 11個月",
      "合約年期 3年",
    ],
    formula: "(4 × $6,000 + $500) × 11月 × 3年",
    total: 808_500,
    note: "清潔服務合約期通常為3年，一旦入選便年年穩定收入，可建立長期合作關係。",
    reference: { label: "例子參考：2026年5月 胡素貞博士紀念學校 — 2026至2029學年學校清潔服務", url: "https://drive.google.com/file/d/1y1Gkq-KMVtVZ9IEsQ_4wN0SgMKgAg9X4/view?usp=sharing" },
  },
];

// ── Count-up animation hook ───────────────────────────────────────────────

// speed = numbers per second
// animate = false means skip animation and jump straight to target
function useCountUp(target: number, speed = 120): number {
  const [count, setCount] = useState(target);
  const rafRef   = useRef<number>(0);
  const prevRef  = useRef(target);

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = target;

    cancelAnimationFrame(rafRef.current);

    // Animate only when transitioning from 0 → real value (fresh fetch).
    // If data was already available (cache hit), target starts non-zero → jump immediately.
    if (prev !== 0 || target <= 0) {
      setCount(target);
      return;
    }

    const duration = Math.max((target / speed) * 1000, 400);
    const start = performance.now();
    const tick = (now: number) => {
      const elapsed  = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased    = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(eased * target));
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setCount(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, speed]);

  return count;
}

// ── Main component ────────────────────────────────────────────────────────

export default function MarketPage() {
  const [selectedCat, setSelectedCat]     = useState("ai-robotics");
  const [hoveredDistrict, setHoveredDistrict] = useState<string | null>(null);
  const [selectedEx, setSelectedEx]       = useState(0);
  const [isDistrictMapVisible, setIsDistrictMapVisible] = useState(false);
  const districtMapRef = useRef<HTMLDivElement>(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const mapElement = districtMapRef.current;
    if (!mapElement) return;

    if (!("IntersectionObserver" in window)) {
      setIsDistrictMapVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsDistrictMapVisible(entry.isIntersecting),
      { threshold: 0.15 },
    );
    observer.observe(mapElement);
    return () => observer.disconnect();
  }, []);

  const { data: stats, isLoading } = useQuery<MarketStats>({
    queryKey: ["market-stats"],
    queryFn: async () => {
      const res = await fetch("/api/stats/market");
      if (!res.ok) throw new Error("載入失敗");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
  });

  const total3mo  = stats?.total3mo  ?? 0;
  const weeklyAvg = stats?.weeklyAvg ?? 0;

  // Count-up: hook animates 0→real value on fresh fetch; jumps immediately on cache hit.
  const animTotal3mo  = useCountUp(total3mo,        200);
  const animWeeklyAvg = useCountUp(weeklyAvg,        10);
  const animSchools   = useCountUp(stats ? 1178 : 0, 200);
  const animCoverage  = useCountUp(stats ? 100  : 0,  33);

  // Category chart data (exclude "others" from interactive buttons)
  const catData = (stats?.categories ?? [])
    .map(c => ({
      ...c,
      name:  CAT_INFO[c.slug]?.name  ?? c.slug,
      color: CAT_INFO[c.slug]?.color ?? "#9ca3af",
      emoji: CAT_INFO[c.slug]?.emoji ?? "📦",
    }))
    .sort((a, b) => b.count - a.count);

  const mainCats = catData.filter(c => c.slug !== "others");
  const selCat   = catData.find(c => c.slug === selectedCat);
  const selCount = selCat?.count ?? 0;
  const selWeek  = Math.round(selCount / 13);
  const selPct   = total3mo > 0 ? Math.round((selCount / total3mo) * 100) : 0;

  // School type — real counts for selected category
  const catSchoolTypes = stats?.categorySchoolTypes?.[selectedCat] ?? [];
  const secondary  = catSchoolTypes.find(s => s.type === "secondary")?.count ?? 0;
  const primary    = catSchoolTypes.find(s => s.type === "primary")?.count   ?? 0;
  const schoolTot  = secondary + primary;
  const secPct     = schoolTot > 0 ? Math.round((secondary / schoolTot) * 100) : 0;
  const priPct     = schoolTot > 0 ? Math.round((primary   / schoolTot) * 100) : 0;

  // District map — filtered by selectedCat when category data is available
  const schoolCountByDistrict = new Map<string, number>(
    (stats?.districts ?? []).map(d => [d.district, d.schoolCount])
  );
  const activeCatDistricts = stats?.categoryDistricts?.[selectedCat] ?? null;
  const activeDistrictRows = activeCatDistricts
    ? activeCatDistricts.map(d => ({ district: d.district, count: d.count, schoolCount: schoolCountByDistrict.get(d.district) ?? 0 }))
    : (stats?.districts ?? []);

  const districtMap = new Map<string, { count: number; schoolCount: number }>(
    activeDistrictRows.map(d => [d.district, { count: d.count, schoolCount: d.schoolCount }])
  );
  const maxCount = Math.max(...activeDistrictRows.map(d => d.count), 1);
  const getBubbleR = (n: number) => 13 + (n / maxCount) * 23;

  const hovDistrict = hoveredDistrict;
  const hovData     = hovDistrict ? districtMap.get(hovDistrict) : null;
  const hovPos      = hovDistrict ? DISTRICT_POS[hovDistrict]    : null;

  const example = VALUE_EX[selectedEx]!;

  return (
    <div className="min-h-screen bg-background">
      {/* ═══ Hero ════════════════════════════════════════════════════════ */}
      <section className="relative bg-gradient-to-br from-[#0a3520] via-[#15803d] to-[#166534] text-white py-20 px-4 overflow-hidden">
        {/* decorative rings */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full border border-white/10" />
          <div className="absolute -right-16 -top-16 w-[350px] h-[350px] rounded-full border border-white/10" />
        </div>

        <div className="container mx-auto max-w-5xl relative">
          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-3">
            全港學校商機數據
          </h1>
          <p className="text-green-200 text-xl font-semibold mb-2">
            追蹤 1,178 間學校的採購動態
          </p>
          <p className="text-green-100/80 text-base mb-4 max-w-xl">
            每日自動爬取全港中小學校採購招標公告，即時掌握您所在行業的商業機遇。
          </p>
          <p className="text-green-300/80 text-sm mb-10">以下為最近90日（3個月）的實時數據統計</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "近3個月標案",    value: `${animTotal3mo.toLocaleString()}+`,  icon: "📋" },
              { label: "每週新增標案",   value: `${animWeeklyAvg}+`,                  icon: "📈" },
              { label: "覆蓋全港學校",   value: `${animSchools.toLocaleString()}間`,  icon: "🏫" },
              { label: "香港18區全覆蓋", value: `${animCoverage}%`,                   icon: "🗺️" },
            ].map(s => (
              <div key={s.label} className="bg-white/10 backdrop-blur rounded-2xl p-4 text-center border border-white/20 hover:bg-white/15 transition-colors">
                <div className="text-3xl mb-1">{s.icon}</div>
                <div className="text-2xl font-bold tabular-nums">{s.value}</div>
                <div className="text-green-200 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* ═══ Section 1 : 行業商機計算機 ══════════════════════════════════ */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2">🏭 行業商機統計</h2>
            <p className="text-muted-foreground">選擇您的行業，即時查看近3個月學校採購數據</p>
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {mainCats.map(c => {
              const active = selectedCat === c.slug;
              return (
                <button
                  key={c.slug}
                  onClick={() => setSelectedCat(c.slug)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border-2 transition-all duration-200 select-none ${
                    active
                      ? "text-white border-transparent shadow-md scale-105"
                      : "bg-white border-border text-muted-foreground hover:border-primary/40"
                  }`}
                  style={active ? { backgroundColor: c.color, borderColor: c.color } : {}}
                >
                  <span>{c.emoji}</span>
                  <span>{c.name}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? "bg-white/25" : "bg-secondary"}`}>
                    {c.count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Stat cards */}
          {selCat && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {[
                {
                  value: selCount,
                  label: `近3個月${selCat.name}標案`,
                  bg: `linear-gradient(135deg, ${selCat.color}, ${selCat.color}bb)`,
                },
                {
                  value: selWeek,
                  label: "平均每週新增商機",
                  bg: "linear-gradient(135deg, #10b981, #059669)",
                },
                {
                  value: `${selPct}%`,
                  label: "佔全部學校標案",
                  bg: "linear-gradient(135deg, #475569, #334155)",
                },
              ].map((card, i) => (
                <div key={i} className="rounded-2xl p-6 text-white text-center shadow-lg" style={{ background: card.bg }}>
                  <div className="text-4xl font-extrabold">{card.value}</div>
                  <div className="text-sm opacity-90 mt-1">{card.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Insight callout */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mb-8">
            <p className="text-sm font-medium text-center leading-relaxed text-[#973c00]">
              「<strong>{selCat?.name}</strong>」類別近三個月共有{" "}
              <strong>{selCount}</strong> 個標案，平均每週新增{" "}
              <strong>{selWeek}</strong> 個商機。
              {schoolTot > 0 && <>其中中學 <strong>{secondary}</strong> 個（{secPct}%）、小學 <strong>{primary}</strong> 個（{priPct}%）。</>}
            </p>
            <p className="font-bold text-center mt-2 italic text-[28px] text-[#e100b8] border-t-[#e100b8] border-r-[#e100b8] border-b-[#e100b8] border-l-[#e100b8]">
              「這不是別人的數據，這就是你正在錯過的生意！」
            </p>
          </div>

          {/* Treemap — area proportional to tender count, no 100% implied */}
          {!isLoading && mainCats.length > 0 && (
            <div className="w-full rounded-2xl overflow-hidden" style={{ height: 320, backgroundColor: "#fefce8" }}>
              <ResponsiveContainer width="100%" height="100%">
                <Treemap
                  data={mainCats}
                  dataKey="count"
                  aspectRatio={16 / 9}
                  // @ts-ignore recharts content prop type mismatch
                  content={(nodeProps: any) => {
                    const { x, y, width, height, name, count, color, emoji, slug } = nodeProps;
                    if (!width || !height || width < 4 || height < 4) return <g />;
                    const active = slug === selectedCat;
                    const textFill = active ? "white" : color;
                    const showLabel  = width > 48 && height > 30;
                    const showCount  = width > 60 && height > 52;
                    return (
                      <g style={{ cursor: "pointer" }} onClick={() => slug && setSelectedCat(slug)}>
                        {/* White base — covers dark SVG background */}
                        <rect x={x + 2} y={y + 2} width={width - 4} height={height - 4} fill="white" rx={8} />
                        {/* Colour tint on top */}
                        <rect
                          x={x + 2} y={y + 2}
                          width={width - 4} height={height - 4}
                          fill={color}
                          fillOpacity={active ? 1 : 0.12}
                          rx={8}
                          stroke={color}
                          strokeOpacity={active ? 1 : 0.35}
                          strokeWidth={active ? 2 : 1}
                        />
                        {showLabel && (
                          <text
                            x={x + width / 2}
                            y={y + height / 2 - (showCount ? 9 : 0)}
                            textAnchor="middle" dominantBaseline="middle"
                            fill={textFill}
                            fontSize={Math.min(13, Math.floor(width / 6))}
                            fontWeight={active ? "700" : "600"}
                            style={{ pointerEvents: "none", userSelect: "none" }}
                          >
                            {emoji} {name}
                          </text>
                        )}
                        {showCount && (
                          <text
                            x={x + width / 2}
                            y={y + height / 2 + 10}
                            textAnchor="middle" dominantBaseline="middle"
                            fill={active ? "rgba(255,255,255,0.85)" : `${color}cc`}
                            fontSize={Math.min(11, Math.floor(width / 7))}
                            style={{ pointerEvents: "none", userSelect: "none" }}
                          >
                            {count}
                          </text>
                        )}
                      </g>
                    );
                  }}
                />
              </ResponsiveContainer>
            </div>
          )}

          <div className="text-center mt-5">
            <Link href={`/tenders?category=${selectedCat}`}>
              <Button variant="outline" className="gap-2 rounded-full px-6">
                查看所有{selCat?.name}標案 →
              </Button>
            </Link>
          </div>
        </div>
      </section>
      {/* ═══ Section 2 : 18區商機地圖 ══════════════════════════════════════ */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2">🗺️ 18區商機地圖</h2>
            <p className="text-muted-foreground mb-3">近3個月各區學校採購標案分佈（移到各區查看詳情）</p>
            <div className="inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-semibold"
              style={{ backgroundColor: `${selCat?.color ?? "#16a34a"}15`, borderColor: `${selCat?.color ?? "#16a34a"}40`, color: selCat?.color ?? "#16a34a" }}>
              {selCat?.emoji} 目前顯示：{selCat?.name} 分類 · {activeDistrictRows.length} 個地區有標案
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 mt-3">
              <p className="font-bold text-center italic text-[28px] text-[#e100b8]">「現左清楚了解商機分佈，重唔快啲黎捉緊機會擴展業務」</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-6 overflow-hidden">
            {/* Legend */}
            <div className="flex flex-wrap justify-center gap-5 mb-6 text-sm">
              {(Object.entries(REGION_LABEL) as [string, string][]).map(([k, label]) => (
                <div key={k} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: REGION_COLOR[k] }} />
                  <span className="text-muted-foreground">{label}</span>
                </div>
              ))}
              <div className="border-l border-border pl-5 flex items-center gap-2 text-xs text-muted-foreground">
                <span>圓圈大小＝標案數目</span>
              </div>
            </div>

            {/* SVG map */}
            <div ref={districtMapRef} className="relative select-none">
              <svg viewBox="0 0 560 430" className="w-full" style={{ fontFamily: "inherit", minHeight: "600px" }}>
                <defs>
                  {/* Radial gradients per region */}
                  {Object.entries(REGION_COLOR).map(([k, color]) => (
                    <radialGradient key={k} id={`rg-${k}`} cx="38%" cy="32%">
                      <stop offset="0%"   stopColor={color} stopOpacity="1"   />
                      <stop offset="100%" stopColor={color} stopOpacity="0.55" />
                    </radialGradient>
                  ))}
                  {/* Glow filters */}
                  <filter id="glow-sm">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="glow-lg">
                    <feGaussianBlur stdDeviation="5" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                  <filter id="shadow">
                    <feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.2" />
                  </filter>
                </defs>

                {/* Map background image */}
                <rect width="560" height="430" fill="#eef2f7" rx="16" />
                <image href="/hk-map.png" x="0" y="0" width="560" height="430" preserveAspectRatio="xMidYMid slice" clipPath="url(#map-clip)" opacity="0.5" />
                <defs>
                  <clipPath id="map-clip">
                    <rect width="560" height="430" rx="16" />
                  </clipPath>
                </defs>

                {/* District bubbles */}
                {Object.entries(DISTRICT_POS).map(([district, pos], index) => {
                  const d = districtMap.get(district);
                  const count = d?.count ?? 0;
                  const r = getBubbleR(count);
                  const color = REGION_COLOR[pos.region];
                  const isHov = hovDistrict === district;

                  return (
                    <g
                      key={district}
                      transform={`translate(${pos.x},${pos.y})`}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={() => setHoveredDistrict(district)}
                      onMouseLeave={() => setHoveredDistrict(null)}
                    >
                      {/* Start the ambient pulse only while the map is visible. */}
                      {isDistrictMapVisible && count > 0 && (
                        <circle r={r} fill="none" stroke={color} strokeWidth="2" opacity="0">
                          <animate
                            attributeName="r"
                            values={`${r};${r + 10}`}
                            dur="2.8s"
                            begin={`${(index % 5) * 0.35}s`}
                            repeatCount="indefinite"
                          />
                          <animate
                            attributeName="opacity"
                            values="0.45;0"
                            dur="2.8s"
                            begin={`${(index % 5) * 0.35}s`}
                            repeatCount="indefinite"
                          />
                        </circle>
                      )}
                      {/* Pulse ring on hover */}
                      {isHov && (
                        <circle r={r + 8} fill="none" stroke={color} strokeWidth="2.5" opacity="0.4" />
                      )}
                      {/* Soft 3D shadow */}
                      <ellipse rx={r * 0.85} ry={r * 0.4} cy={r + 4} fill="#00000022" />
                      {/* Main bubble */}
                      <circle
                        r={r}
                        fill={`url(#rg-${pos.region})`}
                        stroke={color}
                        strokeWidth={isHov ? 2.5 : 1.5}
                        filter={isHov ? "url(#glow-lg)" : count > 40 ? "url(#glow-sm)" : "url(#shadow)"}
                        style={{ transition: "all 0.18s ease" }}
                      />
                      {/* Specular highlight */}
                      <ellipse rx={r * 0.4} ry={r * 0.22} cx={-r * 0.2} cy={-r * 0.35}
                        fill="white" opacity="0.35" />
                      {/* Count text */}
                      <text textAnchor="middle" dominantBaseline="middle"
                        fill="white" fontSize={count >= 10 ? 10 : 8} fontWeight="700"
                        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>
                        {count}
                      </text>
                      {/* District label */}
                      <text textAnchor="middle" y={r + 12} fill="#1e293b" fontSize="8.5" fontWeight="500">
                        {district.replace("區", "")}
                      </text>
                    </g>
                  );
                })}

                {/* Tooltip bubble */}
                {hovDistrict && hovData && hovPos && (() => {
                  const tx = Math.min(Math.max(hovPos.x, 85), 475);
                  const ty = hovPos.y > 310 ? hovPos.y - 80 : hovPos.y + 45;
                  return (
                    <g transform={`translate(${tx},${ty})`} style={{ pointerEvents: "none" }}>
                      <rect x="-72" y="-8" width="144" height="65" rx="10"
                        fill="white" stroke="#e2e8f0" strokeWidth="1.5" filter="url(#glow-sm)" />
                      <text textAnchor="middle" y="12" fill="#0f172a" fontSize="12" fontWeight="700">
                        {hovDistrict}
                      </text>
                      <text textAnchor="middle" y="31" fill="#15803d" fontSize="11" fontWeight="600">
                        📋 {hovData.count} 個標案（近3個月）
                      </text>
                      <text textAnchor="middle" y="48" fill="#64748b" fontSize="10">
                        🏫 {hovData.schoolCount} 間學校
                      </text>
                    </g>
                  );
                })()}
              </svg>
            </div>

            {/* Top 6 rankings (filtered by selected category) */}
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-2">
              {activeDistrictRows.slice(0, 6).map((d, i) => {
                const pos = DISTRICT_POS[d.district];
                const color = pos ? REGION_COLOR[pos.region] : "#6b7280";
                return (
                  <div key={d.district} className="flex items-center gap-1.5 text-sm py-1">
                    <span className="text-muted-foreground text-xs w-4 text-right shrink-0">#{i + 1}</span>
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <span className="font-medium truncate">{d.district}</span>
                    <span className="text-muted-foreground whitespace-nowrap ml-6">{d.count} 個</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
      {/* ═══ Section 3 : 潛在商機價值預估 ════════════════════════════════ */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2">💰 商機計算機預估</h2>
            <p className="text-muted-foreground">選擇您的行業，查看模擬商機計算</p>
          </div>

          {/* Industry tabs */}
          <div className="flex flex-wrap gap-2 justify-center mb-8">
            {VALUE_EX.map((ex, i) => (
              <button
                key={ex.id}
                onClick={() => setSelectedEx(i)}
                className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all duration-200 ${
                  selectedEx === i ? "text-white border-transparent shadow-md" : "bg-white border-border text-muted-foreground hover:border-primary/40"
                }`}
                style={selectedEx === i ? { backgroundColor: ex.color, borderColor: ex.color } : {}}
              >
                {ex.name}
              </button>
            ))}
          </div>

          <div className="rounded-3xl overflow-hidden border shadow-xl">
            {/* Header */}
            <div className="p-6 text-white" style={{ background: `linear-gradient(135deg, ${example.color}ee, ${example.color}99)` }}>
              <div className="text-sm font-semibold opacity-80 mb-1">{example.name.replace(/^\S+\s+/, "")} — 模擬合約價值</div>
              <div className="text-5xl font-extrabold tracking-tight">
                HK$ {(example.total / 1_000_000).toFixed(1)}M
              </div>
              <div className="text-sm opacity-80 mt-2 font-mono">{example.formula}</div>
            </div>

            {/* Steps */}
            <div className="bg-slate-50 p-6 space-y-3">
              {example.steps.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0"
                    style={{ backgroundColor: example.color }}>
                    {i + 1}
                  </div>
                  <span className="text-sm font-medium">{step}</span>
                </div>
              ))}

              {/* Total row */}
              <div className="border-t border-slate-200 pt-4 flex items-center gap-3">
                <div className="w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 font-bold text-sm"
                  style={{ borderColor: example.color, color: example.color }}>
                  =
                </div>
                <span className="text-sm font-semibold text-slate-600">合約總值（模擬）</span>
                <span className="ml-auto font-extrabold text-2xl" style={{ color: example.color }}>
                  HK${example.total.toLocaleString()}
                </span>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mt-2 text-green-800 text-[20px] font-semibold">
                ✅ {example.note}
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 text-blue-700 text-[20px]">
                <strong className="text-[20px]">為何選擇學校標案？</strong>學校為半官方機構，財務受官方規管，付款有保障，
                無需憂心壞帳問題。每份標案均有明確截止日期及清晰規格，供應商可提前規劃業務。
              </div>

              {(example as any).reference && (
                <div className="flex items-start gap-2 pt-1">
                  <span className="text-xs text-muted-foreground shrink-0 mt-0.5">📄</span>
                  <a
                    href={(example as any).reference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline leading-relaxed"
                  >
                    {(example as any).reference.label}
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="container mx-auto max-w-4xl px-4 mt-8">
          <div className="w-full px-6 py-3 rounded-xl border border-yellow-200 bg-yellow-50 text-center">
            <p className="font-bold italic text-[28px] text-[#e100b8]">
              「計好晒數，心動不如行動啦」
            </p>
          </div>
        </div>
      </section>
      {/* ═══ Section 4 : 效率比對表 ══════════════════════════════════════ */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold mb-2">⚡ 採用 Skoltend 的理由</h2>
            <p className="text-muted-foreground">人工搜尋 vs 加入 Skoltend — 哪個選擇更聰明更有效率？</p>
          </div>

          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-100 text-muted-foreground">
                  <th className="text-left p-4 font-semibold">尋找學校商機的方式</th>
                  <th className="p-4 font-semibold text-center">每日耗時</th>
                  <th className="p-4 font-semibold text-center">漏標機率</th>
                  <th className="p-4 font-semibold text-center">資訊管道</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-slate-100">
                  <td className="p-4">
                    <div className="font-semibold">傳統人工自行搜尋</div>
                    <div className="text-xs text-muted-foreground mt-0.5">逐間學校網站查看</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="font-bold text-red-600 text-base">98.17 小時</div>
                    <div className="text-xs text-muted-foreground">(1,178校×5分鐘)</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="font-bold text-red-600 text-base">高達 90%</div>
                    <div className="text-xs text-muted-foreground">資訊極度分散</div>
                  </td>
                  <td className="p-4 text-center text-xs text-muted-foreground">
                    碎片化、極度被動
                  </td>
                </tr>
                <tr className="border-t border-green-100 bg-green-50">
                  <td className="p-4">
                    <div className="font-bold text-green-700 flex items-center gap-1.5">
                      <Zap className="h-4 w-4 fill-current" /> 加入 Skoltend
                    </div>
                    <div className="text-xs text-green-600 mt-0.5">
                      AI 自動爬取 · 電郵及 Telegram 每日推送
                    </div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="font-extrabold text-green-600 text-2xl">0 分鐘</div>
                    <div className="text-xs text-green-600">全自動處理</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="font-extrabold text-green-600 text-2xl">≈ 0%</div>
                    <div className="text-xs text-green-600">全港即時追蹤</div>
                  </td>
                  <td className="p-4 text-center">
                    <div className="text-xs font-semibold text-green-700">一站式、主動精準</div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="bg-slate-50 px-4 py-3 text-xs text-muted-foreground border-t text-center">
              假設 1,178 所學校 × 每間搜尋5分鐘 = 5,890分鐘（98.17小時）才能完成一次全港搜尋
            </div>
          </div>
          <div className="w-full px-6 py-3 rounded-xl border border-yellow-200 bg-yellow-50 text-center mt-8">
            <p className="font-bold italic text-[28px] text-[#e100b8]">
              「最講求效率既香港人，知道點揀啦。」
            </p>
          </div>
        </div>
      </section>
      {/* ═══ CTA ══════════════════════════════════════════════════════════ */}
      <section className="relative py-24 px-4 bg-gradient-to-br from-[#0a3520] via-[#15803d] to-[#166534] text-white text-center overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-white/5" />
        </div>

        <div className="container mx-auto max-w-3xl relative">
          <Target className="h-14 w-14 mx-auto mb-6 text-green-300 opacity-80" />
          <h2 className="text-3xl md:text-4xl font-extrabold mb-4 leading-tight">
            🎯 全港 1,100+ 間學校的採購負責人<br className="hidden md:block" />
            正在尋找供應商
          </h2>

          <div className="w-full mb-10 px-6 py-4 rounded-xl border border-yellow-200 bg-yellow-50 text-center">
            <p className="mb-2 font-bold text-[20px]" style={{ color: "#973c00" }}>
              您的對手已經在看今天的標案了，而您還要繼續等待嗎？
            </p>
            <p className="font-bold italic text-[28px] text-[#e100b8]">
              「本週新增 {weeklyAvg}+ 個商機，錯過一天就是一天的損失。」
            </p>
          </div>

          <Link href="/sign-up">
            <button
              className="relative px-10 py-5 text-2xl md:text-3xl font-extrabold rounded-2xl text-white transition-all duration-300 hover:scale-105 active:scale-95"
              style={{
                background: "linear-gradient(135deg, #fb923c, #ea580c, #c2410c)",
                boxShadow: "0 0 50px rgba(251, 146, 60, 0.55), 0 0 25px rgba(251, 146, 60, 0.35), 0 8px 32px rgba(0,0,0,0.3)",
              }}
            >
              立即免費註冊
            </button>
          </Link>

          <p className="text-green-300/60 text-xs mt-5">
            免費會員即可查看標案標題、地區及截止日期。升級付費解鎖學校名稱及聯絡資料。
          </p>
        </div>
      </section>
    </div>
  );
}
